import React, { useState, useContext, useEffect, useRef } from "react";
import { Link as RouterLink, useHistory } from "react-router-dom";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import InputAdornment from "@material-ui/core/InputAdornment";
import { Helmet } from "react-helmet";
import {
  FaComments,
  FaColumns,
  FaRobot,
  FaBullhorn,
  FaMoneyBillWave,
  FaCogs,
  FaWhatsapp,
  FaInstagram,
  FaFacebookF,
  FaCreditCard,
  FaCode,
} from "react-icons/fa";
import BRFlag from "../../assets/brazil.png";
import USFlag from "../../assets/unitedstates.png";
import ESFlag from "../../assets/esspain.png";
import ARFlag from "../../assets/arabe.png";
import packageJson from "../../../package.json";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import ColorModeContext from "../../layout/themeContext";
import useSettings from "../../hooks/useSettings";
// openApi (e nao api): a tela de login roda sem sessao, e o interceptor
// do api tentaria refresh de token que ainda nao existe.

const languageOptions = [
  { value: "pt-BR", label: "BR", icon: BRFlag },
  { value: "en", label: "EN", icon: USFlag },
  { value: "es", label: "ES", icon: ESFlag },
  { value: "ar", label: "AR", icon: ARFlag },
];

// Fonte unica: a MESMA entrada posiciona o balao e gera a linha dele.
// x/y sao PERCENTUAIS da tela — o SVG usa viewBox 0 0 100 100 esticado,
// entao as coordenadas batem exatamente em qualquer resolucao. Sem isto,
// balao (px) e linha (viewBox esticado) divergem conforme a largura.
// x = distancia da borda (esquerda para features, direita para integracoes).
const FEATURE_PILLS = [
  { icon: <FaComments />, label: "Chat Omnichannel", x: 3, y: 26, delay: 0 },
  { icon: <FaColumns />, label: "Kanban de Vendas", x: 6.5, y: 36, delay: 0.8 },
  { icon: <FaRobot />, label: "Agentes de IA", x: 4.5, y: 46, delay: 1.6 },
  { icon: <FaBullhorn />, label: "Campanhas", x: 7.5, y: 56, delay: 0.4 },
  { icon: <FaMoneyBillWave />, label: "Cobranças PIX", x: 3.5, y: 66, delay: 2.2 },
  { icon: <FaCogs />, label: "Automações", x: 6.5, y: 76, delay: 1.2 },
];

const INTEGRATION_PILLS = [
  { icon: <FaWhatsapp />, label: "WhatsApp", x: 3, y: 30, delay: 1.4 },
  { icon: <FaInstagram />, label: "Instagram", x: 6.5, y: 40, delay: 0.6 },
  { icon: <FaFacebookF />, label: "Facebook", x: 8, y: 50, delay: 2 },
  { icon: <FaCreditCard />, label: "Mercado Pago", x: 5, y: 60, delay: 1 },
  { icon: <FaCode />, label: "API Webhooks", x: 2.5, y: 70, delay: 1.8 },
];

// A linha nasce sob o balao (que e opaco e fica por cima) e converge para
// o centro, onde o card a cobre. Assim ela "sai" do balao sem depender da
// largura do texto — que eu nao teria como medir em %.
const lineFrom = (p, side) =>
  side === "left"
    ? `M ${p.x},${p.y} C 30,${p.y} 38,50 48,50`
    : `M ${100 - p.x},${p.y} C 70,${p.y} 62,50 52,50`;

const useStyles = makeStyles((theme) => {
  const primary = theme.palette.primary.main;
  return {
    root: {
      minHeight: "100vh",
      width: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      position: "relative",
      overflow: "hidden",
      padding: "32px 16px 64px",
      boxSizing: "border-box",
      // Fundo claro com leve tom da cor do tema
      background: `radial-gradient(1200px 600px at 50% -10%, ${primary}14 0%, transparent 60%), linear-gradient(180deg, #ffffff 0%, ${primary}08 100%)`,
    },

    // Linhas que ligam os baloes ao card
    bgLines: {
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
      zIndex: 1,
      pointerEvents: "none",
      // Opacidade fica no proprio traco (strokeOpacity); aqui apagava tudo.
      [theme.breakpoints.down("md")]: { display: "none" },
    },

    // Coluna central (logo + card)
    center: {
      position: "relative",
      zIndex: 2,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      width: "100%",
    },

    // A logo e limitada pela ALTURA (proporcao ~1.83:1, entao a 110px de
    // altura usa ~200px de largura e o maxWidth nunca age). Arquivo original
    // e 677x369 — ha resolucao de sobra, nao pixeliza.
    logoImg: {
      maxWidth: "320px",
      maxHeight: "110px",
      objectFit: "contain",
      display: "block",
      marginBottom: "34px",
      [theme.breakpoints.down("xs")]: {
        maxHeight: "84px",
      },
    },

    card: {
      width: "100%",
      maxWidth: "430px",
      background: "#ffffff",
      borderRadius: "22px",
      padding: "40px 38px 30px",
      boxSizing: "border-box",
      boxShadow: "0 18px 50px rgba(16, 40, 34, 0.10), 0 2px 8px rgba(16, 40, 34, 0.04)",
      [theme.breakpoints.down("xs")]: {
        padding: "30px 22px 24px",
      },
    },

    title: {
      fontSize: "26px",
      fontWeight: 800,
      color: "#111c18",
      letterSpacing: "-0.5px",
      lineHeight: 1.2,
    },

    subtitle: {
      fontSize: "14px",
      color: "#8a95a1",
      marginTop: "6px",
      marginBottom: "26px",
    },

    label: {
      fontSize: "13px",
      fontWeight: 700,
      color: "#31404a",
      marginBottom: "7px",
      display: "block",
    },

    labelRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "7px",
    },

    forgot: {
      fontSize: "12.5px",
      fontWeight: 600,
      color: primary,
      textDecoration: "none",
      "&:hover": { textDecoration: "underline" },
    },

    field: {
      marginBottom: "18px",
      "& .MuiOutlinedInput-root": {
        borderRadius: "11px",
        backgroundColor: "#f6f8f9",
        fontSize: "14.5px",
        "& fieldset": { borderColor: "#e4e9ec" },
        "&:hover fieldset": { borderColor: "#cfd8dd" },
        "&.Mui-focused fieldset": { borderColor: primary, borderWidth: "1.5px" },
        "&.Mui-focused": { backgroundColor: "#fff" },
      },
      "& .MuiOutlinedInput-input": {
        padding: "14.5px 14px",
        color: "#1d2b25",
      },
    },

    remember: {
      margin: "2px 0 22px",
      "& .MuiTypography-body1": {
        fontSize: "13.5px",
        color: "#5b6872",
        fontWeight: 500,
      },
    },

    checkbox: {
      color: "#cfd8dd",
      padding: "6px 8px 6px 0",
      "&.Mui-checked": { color: primary },
    },

    submit: {
      width: "100%",
      padding: "14px 0",
      borderRadius: "999px",
      fontSize: "15.5px",
      fontWeight: 700,
      textTransform: "none",
      color: "#fff",
      background: primary,
      boxShadow: `0 8px 22px ${primary}45`,
      "&:hover": {
        background: theme.palette.primary.dark || primary,
        boxShadow: `0 10px 26px ${primary}5c`,
        transform: "translateY(-1px)",
      },
      transition: "all .2s ease",
    },

    divider: {
      height: "1px",
      background: "#eef1f3",
      margin: "26px 0 18px",
    },

    signupRow: {
      textAlign: "center",
      fontSize: "13.5px",
      color: "#8a95a1",
    },

    signupLink: {
      color: primary,
      fontWeight: 700,
      cursor: "pointer",
      textDecoration: "none",
      marginLeft: "4px",
      "&:hover": { textDecoration: "underline" },
    },

    legal: {
      textAlign: "center",
      marginTop: "12px",
      fontSize: "11.5px",
      color: "#aeb8c0",
      "& a": {
        color: "#aeb8c0",
        textDecoration: "none",
        "&:hover": { textDecoration: "underline" },
      },
    },

    footer: {
      position: "absolute",
      bottom: "18px",
      left: 0,
      right: 0,
      textAlign: "center",
      fontSize: "11px",
      fontWeight: 600,
      letterSpacing: "0.4px",
      color: "#b6c0c7",
      textTransform: "uppercase",
      zIndex: 2,
    },

    // Flutuacao suave: amplitude pequena (7px) para o balao parecer boiar
    // sobre a linha, sem descolar dela.
    "@keyframes float": {
      "0%, 100%": { transform: "translateY(0)" },
      "50%": { transform: "translateY(-7px)" },
    },

    // Pastilhas laterais
    pill: {
      position: "absolute",
      display: "flex",
      alignItems: "center",
      gap: "9px",
      background: "#fff",
      borderRadius: "999px",
      padding: "9px 16px",
      fontSize: "13px",
      fontWeight: 600,
      color: "#31404a",
      whiteSpace: "nowrap",
      boxShadow: "0 8px 22px rgba(16, 40, 34, 0.10)",
      border: "1px solid #eef2f4",
      // Acima das linhas: o balao opaco cobre o inicio da linha, criando a
      // impressao de que ela sai de dentro dele.
      zIndex: 2,
      animation: "$float 5s ease-in-out infinite",
      "& svg": { color: primary, fontSize: "15px" },
      [theme.breakpoints.down("md")]: { display: "none" },
    },

    langSelector: {
      position: "absolute",
      top: "18px",
      right: "20px",
      zIndex: 10,
      display: "flex",
      alignItems: "center",
      gap: "6px",
      cursor: "pointer",
      background: "#fff",
      border: "1px solid #e7ecef",
      borderRadius: "999px",
      padding: "6px 12px",
      fontSize: "12.5px",
      fontWeight: 700,
      color: "#5b6872",
      "&:hover": { borderColor: "#cfd8dd" },
    },

    langDropdown: {
      position: "absolute",
      top: "100%",
      right: 0,
      marginTop: "6px",
      background: "#fff",
      boxShadow: "0 10px 30px rgba(16,40,34,0.12)",
      borderRadius: "10px",
      border: "1px solid #eef2f4",
      padding: "6px",
      minWidth: "132px",
    },

    langOption: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      width: "100%",
      padding: "7px 10px",
      border: "none",
      background: "none",
      borderRadius: "7px",
      fontSize: "13px",
      fontWeight: 600,
      color: "#5b6872",
      cursor: "pointer",
      textAlign: "left",
      "&:hover": { background: "#f4f7f8" },
    },

    flagIcon: {
      width: 18,
      height: 13,
      borderRadius: 3,
      objectFit: "cover",
    },
  };
});

const Login = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { colorMode } = useContext(ColorModeContext);
  const { appLogoFavicon, appName, appLogoDark, appLogoLight } = colorMode;

  const [user, setUser] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [allowSignup, setAllowSignup] = useState(false);
  const [remember, setRemember] = useState(false);
  const { getPublicSetting } = useSettings();
  const { handleLogin, supervisorSelect, enterSupervisorCompany } =
    useContext(AuthContext);

  const [langOpen, setLangOpen] = useState(false);
  const [enabledLanguages, setEnabledLanguages] = useState(["pt-BR", "en"]);
  const langRef = useRef();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setLangOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getCompanyIdFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const companyId = urlParams.get("companyId");
    return companyId ? parseInt(companyId) : null;
  };

  useEffect(() => {
    const companyId = getCompanyIdFromUrl();

    getPublicSetting("userCreation", companyId)
      .then((data) => setAllowSignup(data === "enabled"))
      .catch(() => {});

    getPublicSetting("enabledLanguages", companyId)
      .then((langs) => {
        let arr = ["pt-BR", "en"];
        try {
          if (langs) arr = JSON.parse(langs);
        } catch {}
        setEnabledLanguages(arr);
      })
      .catch(() => setEnabledLanguages(["pt-BR", "en"]));

    // "Lembrar dados de acesso": guarda apenas o e-mail, nunca a senha.
    const savedEmail = localStorage.getItem("rememberedEmail");
    if (savedEmail) {
      setUser((u) => ({ ...u, email: savedEmail }));
      setRemember(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeInput = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const handlSubmit = (e) => {
    e.preventDefault();
    if (remember) {
      localStorage.setItem("rememberedEmail", user.email);
    } else {
      localStorage.removeItem("rememberedEmail");
    }
    handleLogin(user);
  };

  const currentLang =
    languageOptions.find((o) => o.value === i18n.language) || languageOptions[0];

  const handleSelectLang = (opt) => {
    i18n.changeLanguage(opt.value);
    localStorage.setItem("language", opt.value);
    setLangOpen(false);
    window.location.reload();
  };

  const primary = theme.palette.primary.main;

  return (
    <>
      <Helmet>
        <title>{appName || "Zappro"}</title>
        <link rel="icon" href={appLogoFavicon || "/default-favicon.ico"} />
      </Helmet>

      <div className={classes.root}>
        <CssBaseline />

        {/* Linhas decorativas */}
        {/* viewBox 0 0 100 100 esticado = coordenadas em % da tela, iguais as
            dos baloes. vector-effect impede o esticamento de deformar o traco. */}
        <svg
          className={classes.bgLines}
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <g
            fill="none"
            stroke={primary}
            strokeOpacity="0.28"
            strokeWidth="1"
            vectorEffect="non-scaling-stroke"
          >
            {FEATURE_PILLS.map((p) => (
              <path
                key={`l-${p.label}`}
                d={lineFrom(p, "left")}
                vectorEffect="non-scaling-stroke"
              />
            ))}
            {INTEGRATION_PILLS.map((p) => (
              <path
                key={`r-${p.label}`}
                d={lineFrom(p, "right")}
                vectorEffect="non-scaling-stroke"
              />
            ))}
          </g>
        </svg>

        {/* Baloes — recursos (esquerda). translateY(-50%) centra o balao na
            altura exata onde a linha nasce. */}
        {FEATURE_PILLS.map((p) => (
          <div
            key={p.label}
            className={classes.pill}
            style={{
              top: `${p.y}%`,
              left: `${p.x}%`,
              marginTop: "-19px",
              animationDelay: `${p.delay}s`,
            }}
          >
            {p.icon}
            {p.label}
          </div>
        ))}

        {/* Baloes — integrações (direita) */}
        {INTEGRATION_PILLS.map((p) => (
          <div
            key={p.label}
            className={classes.pill}
            style={{
              top: `${p.y}%`,
              right: `${p.x}%`,
              marginTop: "-19px",
              animationDelay: `${p.delay}s`,
            }}
          >
            {p.icon}
            {p.label}
          </div>
        ))}

        {/* Seletor de idioma */}
        <div ref={langRef} style={{ position: "absolute", top: 18, right: 20, zIndex: 10 }}>
          <button className={classes.langSelector} onClick={() => setLangOpen((o) => !o)}>
            <img src={currentLang.icon} alt={currentLang.label} className={classes.flagIcon} />
            {currentLang.label}
            <span style={{ fontSize: 9, color: "#aeb8c0" }}>▼</span>
          </button>
          {langOpen && (
            <div className={classes.langDropdown}>
              {languageOptions
                .filter((o) => enabledLanguages.includes(o.value))
                .map((opt) => (
                  <button
                    key={opt.value}
                    className={classes.langOption}
                    onClick={() => handleSelectLang(opt)}
                  >
                    <img src={opt.icon} alt={opt.label} className={classes.flagIcon} />
                    {opt.label}
                  </button>
                ))}
            </div>
          )}
        </div>

        {/* Logo + card */}
        <div className={classes.center}>
          <img
            className={classes.logoImg}
            src={appLogoLight || appLogoDark}
            alt={appName || "Logo"}
          />

          <div className={classes.card}>
            <Typography className={classes.title}>
              Faça o login na sua conta
            </Typography>
            <Typography className={classes.subtitle}>
              CRM e Atendimento Omnichannel
            </Typography>

            <form noValidate onSubmit={handlSubmit}>
              <label className={classes.label}>E-mail</label>
              <TextField
                variant="outlined"
                required
                fullWidth
                id="email"
                placeholder="seu@email.com"
                name="email"
                value={user.email}
                onChange={handleChangeInput}
                autoComplete="email"
                autoFocus
                className={classes.field}
              />

              <div className={classes.labelRow}>
                <label className={classes.label} style={{ marginBottom: 0 }}>
                  Senha
                </label>
                <RouterLink to="/esqueceu-senha" className={classes.forgot}>
                  Esqueceu a senha?
                </RouterLink>
              </div>
              <TextField
                variant="outlined"
                required
                fullWidth
                name="password"
                placeholder="Sua senha secreta"
                type={showPassword ? "text" : "password"}
                id="password"
                value={user.password}
                onChange={handleChangeInput}
                autoComplete="current-password"
                className={classes.field}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((s) => !s)}
                        edge="end"
                        size="small"
                        style={{ color: "#aeb8c0" }}
                      >
                        {showPassword ? (
                          <VisibilityOff fontSize="small" />
                        ) : (
                          <Visibility fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <FormControlLabel
                className={classes.remember}
                control={
                  <Checkbox
                    className={classes.checkbox}
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    size="small"
                  />
                }
                label="Lembrar dados de acesso"
              />

              <Button type="submit" fullWidth className={classes.submit}>
                Entrar
              </Button>
            </form>

            <div className={classes.divider} />

            {allowSignup && (
              <div className={classes.signupRow}>
                Ainda não tem conta?
                <a className={classes.signupLink} onClick={() => history.push("/signup")}>
                  Cadastre-se grátis
                </a>
              </div>
            )}

            <div className={classes.legal}>
              <RouterLink to="/termos-de-uso">Termos de Uso</RouterLink>
              {" · "}
              <RouterLink to="/politica-de-privacidade">Política de Privacidade</RouterLink>
            </div>
          </div>
        </div>

        <div className={classes.footer}>
          © {new Date().getFullYear()} {appName || "Zappro"} — Todos os direitos
          reservados · v{packageJson.version}
        </div>
      </div>

      {supervisorSelect && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(17,28,24,0.55)",
            zIndex: 99999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 18,
              padding: 30,
              width: 380,
              maxWidth: "90%",
              boxShadow: "0 20px 60px rgba(16,40,34,0.25)",
            }}
          >
            <h3 style={{ color: "#111c18", margin: "0 0 6px", fontSize: 19, fontWeight: 800 }}>
              Selecione a empresa
            </h3>
            <p style={{ color: "#8a95a1", fontSize: 13, margin: "0 0 18px" }}>
              Você tem acesso a {supervisorSelect.companies.length} empresa(s).
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 320, overflowY: "auto" }}>
              {supervisorSelect.companies.map((c) => (
                <button
                  key={c.companyId}
                  onClick={() => enterSupervisorCompany(c.companyId)}
                  style={{
                    padding: "13px 15px",
                    borderRadius: 11,
                    border: "1px solid #e7ecef",
                    background: "#f6f8f9",
                    color: "#1d2b25",
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  {c.companyName}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Login;
